import { useContext } from "react";
import { DriveContext } from "@/store/DriveContext";
import { useNavigate } from "react-router-dom";

function MainContent() {
  const { user, onlinePlayersDetails } = useContext(DriveContext)!;
  const navigate = useNavigate();

  return (
    <>
      <div className="flex items-center gap-2 px-4 sm:px-6 h-10 sm:h-11 bg-[#0a0a12]/70 border-b border-violet-500/10 shrink-0 overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
        <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide shrink-0 flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full shadow-[0_0_6px_rgba(34,197,94,0.7)] shrink-0" /> 
          Live Area
        </div>
        
        <div className="w-px h-[18px] bg-violet-500/15 shrink-0 mx-1" />
        
        {onlinePlayersDetails && onlinePlayersDetails.length > 0 ? (
          onlinePlayersDetails.map((player, idx) => (
            <div key={idx} className="flex items-center gap-1.5 py-0.5 px-2.5 pl-1 bg-violet-500/10 border border-violet-500/15 rounded-full whitespace-nowrap shrink-0">
              <div className="w-5 h-5 bg-gradient-to-br from-violet-500 to-indigo-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0">
                {player?.username?.[0]?.toUpperCase() ?? "?"}
              </div>
              <span className="text-[11px] font-medium text-gray-400">{player?.username || "Unknown"}</span>
            </div>
          ))
        ) : (
          <span className="text-[11px] text-gray-500 font-medium ml-1">Waiting for players...</span>
        )}
      </div>

      {/* Main dashboard content */}
      <div className="flex-1 flex flex-col p-4 sm:p-6 gap-3 sm:gap-4 overflow-hidden relative z-10">
        
        {/* Personal Stats Row */}
        <div>
          <h2 className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Your Stats</h2>
          <div className="grid grid-cols-2 gap-2 shrink-0">
            <div className="bg-[#101020]/90 border border-violet-500/15 rounded-xl p-2.5 flex flex-col text-left">
              <span className="text-[9px] font-semibold text-gray-600 uppercase tracking-wide mb-0.5">Rating</span>
              <span className="text-[22px] sm:text-[26px] font-extrabold text-[#f1f0ff] leading-none tracking-tight">{user?.rating || 0}</span>
            </div>
            <div className="bg-[#101020]/90 border border-violet-500/15 rounded-xl p-2.5 flex flex-col text-left">
              <span className="text-[9px] font-semibold text-gray-600 uppercase tracking-wide mb-0.5">Streak</span>
              <span className="text-[22px] sm:text-[26px] font-extrabold text-amber-400 leading-none tracking-tight">{user?.streak || 0}🔥</span>
            </div>
          </div>
        </div>

        {/* Game Mode Cards Area */}
        <div className="flex flex-col flex-1 min-h-0">
          <h2 className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mt-1.5 mb-2">Arena Modes</h2>
          
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5 flex-1 min-h-0">
            <div 
              onClick={() => navigate("/duel")}
              className="group relative bg-[#101020]/90 border border-violet-500/15 rounded-2xl cursor-pointer flex flex-col items-center justify-center gap-1.5 p-3 min-h-0 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-[0_10px_28px_rgba(0,0,0,0.4),0_0_0_1px_rgba(139,92,246,0.15)] active:scale-[0.98]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <span className="absolute top-2 right-2 text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-widest bg-red-500/15 text-red-300">Hot</span>
              <div className="text-[28px] sm:text-[34px] leading-none drop-shadow-[0_0_8px_rgba(139,92,246,0.3)]">⚔️</div>
              <div className="text-[13px] sm:text-[15px] font-bold text-[#f1f0ff] text-center tracking-tight leading-snug">Math Duel</div>
              <div className="text-[10px] sm:text-[11px] text-gray-500 text-center leading-snug">Head-to-head 1v1 action</div>
            </div>
            
            <div 
              onClick={() => navigate("/fastest")}
              className="group relative bg-[#101020]/90 border border-violet-500/15 rounded-2xl cursor-pointer flex flex-col items-center justify-center gap-1.5 p-3 min-h-0 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-[0_10px_28px_rgba(0,0,0,0.4),0_0_0_1px_rgba(139,92,246,0.15)] active:scale-[0.98]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="text-[28px] sm:text-[34px] leading-none drop-shadow-[0_0_8px_rgba(139,92,246,0.3)]">⚡</div>
              <div className="text-[13px] sm:text-[15px] font-bold text-[#f1f0ff] text-center tracking-tight leading-snug">Fastest Finger</div>
              <div className="text-[10px] sm:text-[11px] text-gray-500 text-center leading-snug">Speed determines winner</div>
            </div>
            
            <div 
              onClick={() => navigate("/memory")}
              className="group relative bg-[#101020]/90 border border-violet-500/15 rounded-2xl cursor-pointer flex flex-col items-center justify-center gap-1.5 p-3 min-h-0 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-[0_10px_28px_rgba(0,0,0,0.4),0_0_0_1px_rgba(139,92,246,0.15)] active:scale-[0.98]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="text-[28px] sm:text-[34px] leading-none drop-shadow-[0_0_8px_rgba(139,92,246,0.3)]">🧠</div>
              <div className="text-[13px] sm:text-[15px] font-bold text-[#f1f0ff] text-center tracking-tight leading-snug">Math Memory</div>
              <div className="text-[10px] sm:text-[11px] text-gray-500 text-center leading-snug">Test your working memory</div>
            </div>
            
            <div 
              onClick={() => navigate("/flash")}
              className="group relative bg-[#101020]/90 border border-violet-500/15 rounded-2xl cursor-pointer flex flex-col items-center justify-center gap-1.5 p-3 min-h-0 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-[0_10px_28px_rgba(0,0,0,0.4),0_0_0_1px_rgba(139,92,246,0.15)] active:scale-[0.98]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <span className="absolute top-2 right-2 text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-widest bg-purple-500/15 text-purple-300">New</span>
              <div className="text-[28px] sm:text-[34px] leading-none drop-shadow-[0_0_8px_rgba(139,92,246,0.3)]">🚀</div>
              <div className="text-[13px] sm:text-[15px] font-bold text-[#f1f0ff] text-center tracking-tight leading-snug">Flash Mode</div>
              <div className="text-[10px] sm:text-[11px] text-gray-500 text-center leading-snug">Blink and you miss it</div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

export default MainContent;